import { useActor } from '@ui-common/hooks/useActor'
import { shallowEqual, useSelector } from '@xstate/react'
import { useCallback } from 'react'
import { AnyStateMachine, BaseActorRef, MachineContext, MachineImplementationsFrom } from 'xstate'

import { formDataStepMachine, FormStepActions, SUBMIT_FAILED, SUBMITTING } from './formDataStepMachine'
import { PromptEvent } from './promptMachine'
import { EditableMachineContext } from './types'

export interface FormStepState<TContext extends MachineContext, TStepOutputDataType> {
  context: TContext
  submit: (data: TStepOutputDataType) => void
  abort: () => void
  showInfo: () => void
  back: (hasUnsavedChanges: boolean) => void
  isSubmitting: boolean
  hasSubmitFailed: boolean
  abortPromptMachine?: BaseActorRef<PromptEvent>
  infoPromptMachine?: BaseActorRef<PromptEvent>
  reEditPromptMachine?: BaseActorRef<PromptEvent>
}

export const useFormStepState = <
  TContext extends MachineContext & EditableMachineContext,
  TStepOutputDataType extends object,
>(
  input: TContext,
  parentActions: FormStepActions<TStepOutputDataType>,
  machineImplementations?: Partial<MachineImplementationsFrom<AnyStateMachine>>,
): FormStepState<TContext, TStepOutputDataType> => {
  const machine = formDataStepMachine<TContext, TStepOutputDataType>(parentActions).provide(
    machineImplementations ?? {},
  )

  const actor = useActor(machine, { input })

  const currentState = useSelector(actor, (snapshot) => snapshot.value)
  const context = useSelector(actor, (snapshot) => snapshot.context, shallowEqual)
  const childActors = useSelector(actor, (snapshot) => snapshot.children, shallowEqual)
  const { abortPromptMachine, infoPromptMachine, reEditPromptMachine } = childActors
  const isSubmitting = currentState === SUBMITTING
  const hasSubmitFailed = currentState === SUBMIT_FAILED

  const back = useCallback(
    (hasUnsavedChanges: boolean = false) => {
      if (context.isEditing && hasUnsavedChanges) {
        actor.send({ type: 'SHOW_CANCEL_REEDIT_PROMPT' })
      } else {
        actor.send({ type: 'BACK' })
      }
    },
    [actor, context.isEditing],
  )

  return {
    context,
    abort: () => actor.send({ type: 'SHOW_ABORT_PROMPT' }),
    showInfo: () => actor.send({ type: 'SHOW_INFO_PROMPT' }),
    back,
    submit: (data: TStepOutputDataType) => actor.send({ type: 'SUBMIT', data }),
    isSubmitting,
    hasSubmitFailed,
    abortPromptMachine,
    infoPromptMachine,
    reEditPromptMachine,
  }
}
